import React, {createContext, useCallback, useContext, useEffect, useState} from "react";

export interface IAddToPackageContext {
    addedIds: number[];
    selectedIds: number[];
    addIds: (ids: number[]) => void;
    toggleSelectedId: (id: number) => void;
    selectIds: (ids: number[]) => void;
    clearSelectedIds: (ids?: number[]) => void;
    resetSelectedIds: () => void;
    getSelectionVersion: () => number;
    isSelectionVersionCurrent: (version: number) => boolean;
}

interface AddToPackageProviderProps {
    children: React.ReactNode;
    packageGenerationMessage?: string;
    packageOperationInProgress?: boolean;
    packageItemIds?: number[];
    selectionScopeKey?: string;
}

const AddToPackageContext =
    createContext<IAddToPackageContext | undefined>(undefined);

export const AddToPackageProvider: React.FC<
    AddToPackageProviderProps
> = ({
    children,
    packageGenerationMessage,
    packageOperationInProgress,
    packageItemIds,
    selectionScopeKey,
}) => {
    const [addedIds, setAddedIds] = useState<number[]>([]);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const selectionVersion = React.useRef(0);

    useEffect(() => {
        const generationCompleted = packageGenerationMessage === "packageGeneratingMessage" && !packageOperationInProgress;

        if (generationCompleted) {
            setAddedIds([]);
            return;
        }

        setAddedIds(packageItemIds ?? []);
    }, [packageGenerationMessage, packageOperationInProgress, packageItemIds]);

    const addIds = useCallback((ids: number[]) => {
        setAddedIds(previousIds => {
            const mergedIds = new Set(previousIds);

            ids.forEach(id => {
                if (typeof id === "number" && !Number.isNaN(id)) {
                    mergedIds.add(id);
                }
            });

            return Array.from(mergedIds);
        });
    }, []);

    const resetSelectedIds = useCallback(() => {
        selectionVersion.current += 1;
        setSelectedIds([]);
    }, []);

    React.useEffect(() => {
        resetSelectedIds();
    }, [selectionScopeKey, resetSelectedIds]);

    const toggleSelectedId = useCallback((id: number) => {
        if (!Number.isFinite(id) || addedIds.includes(id)) {
            return;
        }

        setSelectedIds(previousIds => previousIds.includes(id)
            ? previousIds.filter(selectedId => selectedId !== id)
            : [...previousIds, id]);
    }, [addedIds]);

    const selectIds = useCallback((ids: number[]) => {
        setSelectedIds(previousIds => {
            const nextIds = new Set(previousIds);
            ids.forEach(id => {
                if (Number.isFinite(id) && !addedIds.includes(id)) {
                    nextIds.add(id);
                }
            });
            return Array.from(nextIds);
        });
    }, [addedIds]);

    const clearSelectedIds = useCallback((ids?: number[]) => {
        if (!ids) {
            resetSelectedIds();
            return;
        }

        const idsToClear = new Set(ids);
        setSelectedIds(previousIds => previousIds.filter(id => !idsToClear.has(id)));
    }, [resetSelectedIds]);

    const getSelectionVersion = useCallback(() => selectionVersion.current, []);
    const isSelectionVersionCurrent = useCallback((version: number) => selectionVersion.current === version, []);

    return (
        <AddToPackageContext.Provider value={{
            addedIds,
            selectedIds,
            addIds,
            toggleSelectedId,
            selectIds,
            clearSelectedIds,
            resetSelectedIds,
            getSelectionVersion,
            isSelectionVersionCurrent,
        }}>
            {children}
        </AddToPackageContext.Provider>
    );
};

export const useAddToPackageContext = (): IAddToPackageContext => {
    const context = useContext(AddToPackageContext);

    if (!context) {
        throw new Error("useAddToPackageContext must be used within AddToPackageProvider");
    }

    return context;
};